import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';

function BeerLocationAvailabilitySelector({
  locations,
  selectedIds,
  onSelectedIdsChange,
  loading,
  disabled,
  validationError,
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleToggle = useCallback(
    (locationId) => {
      const nextSet = new Set(selectedIds);
      if (nextSet.has(locationId)) {
        nextSet.delete(locationId);
      } else {
        nextSet.add(locationId);
      }
      onSelectedIdsChange(Array.from(nextSet));
    },
    [onSelectedIdsChange, selectedIds],
  );

  const locationToggleHandlers = useMemo(() => {
    const handlers = {};
    locations.forEach((location) => {
      handlers[location.id] = () => handleToggle(location.id);
    });
    return handlers;
  }, [handleToggle, locations]);

  const summaryText =
    selectedIds.length === 1
      ? 'Listing at 1 location'
      : `Listing at ${selectedIds.length} locations`;

  if (loading) {
    return <CircularProgress size={24} sx={{ my: 1 }} />;
  }

  if (locations.length <= 1) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        List at locations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Choose where this beer should appear. Shared beer details apply wherever it is listed.
      </Typography>
      {validationError ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {validationError}
        </Alert>
      ) : null}
      <FormGroup>
        {locations.map((location) => (
          <FormControlLabel
            key={location.id}
            control={
              <Checkbox
                checked={selectedSet.has(location.id)}
                disabled={disabled}
                onChange={locationToggleHandlers[location.id]}
              />
            }
            label={location.label}
          />
        ))}
      </FormGroup>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {summaryText}
      </Typography>
    </Box>
  );
}

BeerLocationAvailabilitySelector.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectedIdsChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  validationError: PropTypes.string,
};

BeerLocationAvailabilitySelector.defaultProps = {
  loading: false,
  disabled: false,
  validationError: '',
};

export default BeerLocationAvailabilitySelector;
